<template>
    <div class="pdf-viewer" :class="merge ? `merge` : ``" @click="hideContextMenu()">
        <header class="pdf-viewer__header" v-if="!showSelectorAddFileModal">
            <div class="left">
                <div class="d-flex justify-content-start align-items-center">
                    <label v-if="!renameWorkspace" style="width: 150px;"
                           class="d-none d-xl-block">Select{{ currentWorkspace || documentId ? `ed` : `` }}
                        {{ merge ? `Bundle` : `File` }}:</label>
                    <label v-else style="width: 150px;" class="d-none d-xl-block">Rename Bundle:</label>
                    <div v-if="!merge" class="input-belt mr-3">
                        <el-select v-model="documentId" placeholder="Select Document" class="col-12" id="v-step-0">
                            <el-option
                                v-for="item in docs.filter(document => document.uploaded)"
                                :key="item.id"
                                :label="item.filename"
                                :value="item.id"
                            ></el-option>
                        </el-select>
                    </div>
                    <div v-if="merge" class="d-flex align-items-center justify-content-between mr-5">
                        <el-select v-if="!renameWorkspace" v-model="currentWorkspace" @change="updateWorkspace"
                                   placeholder="Select Bundle" class="col-12" id="v-step-0">
                            <el-option
                                v-for="item in workspaces"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id"
                            ></el-option>
                        </el-select>
                        <div v-else-if="workspace && renameWorkspace"
                             class="d-flex align-items-center justify-content-between col-12">
                            <application-input ref="workspaceName" v-model="workspace.name"
                                               class="input-belt mr-3"></application-input>
                            <a @click.prevent.stop="update" v-b-tooltip.hover title="Update Title">
                                <font-awesome-icon :icon="['fas', 'check']"/>
                            </a>
                        </div>
                        <a @click.prevent.stop="editWorkspace" v-b-tooltip.hover title="Edit Title"
                           v-if="!renameWorkspace && currentWorkspace">
                            <font-awesome-icon :icon="['fas', 'pencil-alt']"/>
                        </a>
                    </div>
                </div>
                <a v-if="merge" @click.prevent.stop="addWorkspace" class="success" v-b-tooltip.hover
                   title="Create New Bundle">
                    <font-awesome-icon :icon="['fas', 'plus']"/>
                </a>
                <button v-if="merge && currentWorkspace && isPageOrderable" @click.prevent.stop="togglePageOrderandSave"
                        class="btn-danger ml-2 icon with-text" v-b-tooltip.hover title="Exit Reorder Mode">Exit Reorder
                    Mode
                </button>
                <button v-if="merge && currentWorkspace && isAnnotating" @click.prevent.stop="togglePageEditandSave"
                        class="btn-danger ml-2 icon with-text" v-b-tooltip.hover title="Exit Annotation Mode">Exit Edit
                    Mode
                </button>

                <a v-if="documentId" @click.prevent.stop="togglePreview" :disabled="isAddingSignature" v-b-tooltip.hover
                   title="Show Sidebar" :class="{'active': isPreviewEnabled}"><i class="mdi mdi-view-list"></i></a>
                <a v-if="documentId" @click.prevent.stop="toggleDoubleView" :disabled="isAddingSignature"
                   :class="{'active': doubleEnabled}" v-b-tooltip.hover title="View Side By Side"><i
                    class="mdi mdi-format-columns"></i></a>
                <a v-if="documentId" @click.prevent.stop="toggleAddingSignature" :class="{'active': isAddingSignature}"
                   v-b-tooltip.hover title="Add Signatures">
                    <font-awesome-icon :icon="['fas', 'file-signature']"/>
                </a>
            </div>
            <div class="center">
                <PDFZoom
                    v-if="documentId && !merge"
                    :scale="scale"
                    :fit="fit"
                    class="header-item"
                />
            </div>
            <div class="right">
                <div v-if="currentWorkspace && !isPageOrderable && !isAnnotating"
                     class="col-12 d-flex align-items-center justify-content-end">
                    <a class="btn-green mx-3" :disabled="saving" v-b-tooltip.hover title="Save & Exit"
                       @click="showSaveAndExitModal = true">
                        <font-awesome-icon :icon="['fas', 'door-open']"/>
                    </a>
                    <a class="btn-green mx-3" :disabled="saving" v-b-tooltip.hover title="Save & Compile Bundle"
                       @click="showSaveAndCompileModal = true">
                        <font-awesome-icon :icon="['far', 'save']"/>
                    </a>
                    <a v-b-tooltip.hover title="Delete Bundle" class="btn-red mr-3" @click="deleteWorkspace">
                        <font-awesome-icon :icon="['fas', 'trash']"/>
                    </a>
                </div>
                <PDFPaginator
                    v-if="documentId  && !merge"
                    v-model="currentPage"
                    :pageCount="pageCount"
                    class="header-item"
                />

                <button v-if="documentId && !merge" class="btn-green" @click="close">Done</button>
            </div>
            <slot name="header"></slot>
        </header>

        <PDFData
            v-if="documentId"
            class="pdf-viewer__main"
            :url="documentUrl"
            @document-rendered="onDocumentRendered"
            @document-errored="onDocumentErrored"
            :key="documentId"
        >
            <template v-slot:preview="{pages}">
                <PDFPreview
                    v-show="isPreviewEnabled"
                    class="pdf-viewer__preview"
                    v-bind="{merge, pages, scale, currentPage, pageCount, isPreviewEnabled, isPageOrderable, isAddingSignature, isAnnotating, signatures}"
                />
            </template>

            <template v-slot:document="{pages}">
                <PDFDocument
                    class="pdf-viewer__document"
                    :class="{ 'preview-enabled': isPreviewEnabled }"
                    v-bind="{merge, headers, pages, scale, optimalScale, fit, currentPage, pageCount, isPreviewEnabled, contacts, applicants, doubleEnabled, isPageOrderable, isAddingSignature, isAnnotating, isApplicantSet, documentData}"
                    @select-applicant="selectApplicant"
                />
            </template>
        </PDFData>

        <PDFWorkspace
            :key="currentWorkspace"
            v-if="currentWorkspace && merge"
            v-bind="{currentWorkspace, allDocs, docs, headers, workspaceDocuments, pageCount, isPreviewEnabled, isPageOrderable, doubleEnabled, isAnnotating, documentSidebar, imageObjects, currentPage, showSuggestions}">
            <PDFDataMultiple
                class="pdf-viewer__main "
                :class="documentSidebar ? `col-10` : `col-12`"
                v-bind="{workspace, workspaceDocuments, workspaceDocumentsLoaded}"
                @document-rendered="onDocumentRendered"
                @document-errored="onDocumentErrored"
                v-loading="documentsOrdered"
                element-loading-text="Preparing workspace..."
                element-loading-background="rgba(239, 239, 239, 1)">
                <template v-slot:preview="{pages, pdfDocs}">
                    <PDFPreview
                        v-show="isPreviewEnabled || isPageOrderable"
                        class="pdf-viewer__preview"
                        v-bind="{pages, pdfDocs, scale, currentPage, pageCount, isPreviewEnabled, isPageOrderable, isAddingSignature, signatures, isAnnotating, merge, showSuggestions}"

                    />
                </template>
                <template v-slot:document="{pages, pdfDocs}">
                    <PDFDocument
                        v-show="!isPageOrderable"
                        class="pdf-viewer__document"
                        :class="{ 'preview-enabled': isPreviewEnabled }"
                        v-bind="{headers, pages, pdfDocs, scale, optimalScale, fit, currentPage, pageCount, isPreviewEnabled, contacts, applicants, doubleEnabled, isPageOrderable, isAddingSignature, isAnnotating, annotations, isApplicantSet, documentData, workspace}"
                        @select-applicant="selectApplicant"
                    />
                </template>
            </PDFDataMultiple>
        </PDFWorkspace>
        <x-modal width="595" style="margin-top: 5rem!important;" v-if="showQuickPagePreview"
                 @close="closeQuickPagePreview" :footer="false">
            <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                Preview
            </div>
            <div class="container" style="
              max-height: 70vh;
              overflow-y: scroll;
          ">
                <img :src="quickPagePreviewSrc" class="img-fluid w-100"/>
            </div>
        </x-modal>
        <x-modal width="500" v-if="showSelectorAddFileModal && !merge" :footer="false" @close="$emit('close', true)">
            <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                Select Document to Add Signatures
            </div>
            <div class="text-center">
                <div v-if="!merge" class="input-belt mr-3">
                    <el-select v-model="documentId" placeholder="Select Document" class="col-12" id="v-step-0">
                        <el-option
                            v-for="item in docs.filter(document => document.uploaded)"
                            :key="item.id"
                            :label="item.filename"
                            :value="item.id"
                        ></el-option>
                    </el-select>
                </div>
            </div>
        </x-modal>

        <x-modal
            width="500"
            :minHeight="200"
            v-if="showSelectorAddFileModal && merge"
            :footer="false"
            @close="$bus.$emit('pdf::close::merge', true)">

            <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                Select or Create Document Bundle
            </div>
            <div class="text-center">
                <el-select v-if="!renameWorkspace && workspaces.length > 0" v-model="currentWorkspace"
                           @change="updateWorkspace" placeholder="Select Bundle" class="col-12" id="v-step-0">
                    <el-option
                        v-for="item in workspaces"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                    ></el-option>
                </el-select>

                <hr v-if="workspaces.length > 0"/>

                <a @click.prevent.stop="addWorkspace" class="btn btn-success">Create New Bundle</a>
            </div>
        </x-modal>
        <x-modal width="300" :key="contacts.length" v-if="showApplicantSelectionModal"
                 @close="showApplicantSelectionModal=false" @cancel="showApplicantSelectionModal=false"
                 :okText="`Insert`" @ok="setApplicant">
            <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                Ambition Sign
            </div>
            <div>
                <el-select
                    v-model="setApplicantName"
                    placeholder="Signature request from"
                    class="w-100"
                    value-key='id'
                    clearable
                    v-b-tooltip.hover title="Request a signature"
                    popper-class="document-types">
                    <el-option-group
                        :key="group" v-for="group in contactsGroup.keys()"
                        :label="group">
                        <el-option
                            v-for="item in contactsGroup.get(group)"
                            :key="item.id"
                            :label="`${item.first_name} ${item.last_name}`"
                            :value="item">
                        </el-option>
                    </el-option-group>
                    <el-option-group :label="`Add New`">
                        <el-option
                            key="add_signer"
                            label="Third Party"
                            value="add_new_signer"
                        ></el-option>
                    </el-option-group>
                </el-select>

                <div class="new-signer-form" v-if="setApplicantName == 'add_new_signer'">
                    <h4>Create new signer</h4>
                    <el-form ref="form" :model="newSigner">
                        <el-form-item>
                            <el-input placeholder="First Name" v-model="newSigner.first_name"></el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-input placeholder="Last Name" v-model="newSigner.last_name"></el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-input placeholder="Email" :class="{error: verifyNewSigner(newSigner)}"
                                      v-model="newSigner.email"></el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-input placeholder="Phone" v-model="newSigner.phone"></el-input>
                        </el-form-item>
                    </el-form>
                </div>
            </div>
        </x-modal>
        <x-modal
            width="500"
            :minHeight="220"
            v-if="showSaveAndExitModal"
            @close="showSaveAndExitModal = false"
            @cancel="showSaveAndExitModal = false"
            @ok="saveAndExit"
            :okText="`Save & Exit`">
            <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                Save & Exit
            </div>
            <div>
                <label>Document Name:</label>
                <input class="pdf-document-input mb-3" v-model="workspace.name"></input>
            </div>
        </x-modal>
        <x-modal
            width="500"
            v-if="showSaveAndCompileModal"
            @close="showSaveAndCompileModal = false"
            @cancel="showSaveAndCompileModal = false"
            @ok="saveAndCompile()"
            :okText="compileAvailable ? `Compile` : `Save`"
            cancelText="Cancel">
            <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                Save and Compile Document
            </div>
            <div>
                <label>Document Name:</label>
                <input class="pdf-document-input mb-3" v-model="workspace.name"></input>
                <label>Export as:</label>
                <el-select
                    v-model="selectedDocumentType"
                    filterable
                    clearable
                    class="pdf-document-popper mb-3"
                    placeholder="Select Document Type"
                    popper-class="pdf-document-dropdown"
                >
                    <el-option
                        v-for="item in documentTypes"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                    ></el-option>
                </el-select>

                <div v-if="hasSignatures" class="please-note">
                    <small><strong>*This document contains areas for customer signatures, the final compiled document
                        will be available to be sent for Digital Signing.</strong></small>
                </div>
            </div>
        </x-modal>
        <PDFPaginator
            v-if="currentWorkspace && merge"
            v-model="pageIndex"
            :pageCount="pageCount"
            class="merge-pagination"
        />
    </div>
</template>

<script>
import PDFDocument from "./PDFDocument"
import PDFData from "./PDFData"
import PDFDataMultiple from "./PDFDataMultiple"
import PDFPaginator from "./PDFPaginator"
import PDFPreview from "./PDFPreview"
import PDFZoom from "./PDFZoom"
import PDFWorkspace from "./PDFWorkspace"
import {mapGetters} from "vuex"

function floor(value, precision) {
    const multiplier = Math.pow(10, precision || 0)
    return Math.floor(value * multiplier) / multiplier
}

function groupBy(list, keyGetter) {
    const map = new Map()
    list.forEach((item) => {
        const key = keyGetter(item)
        const collection = map.get(key)
        if (!collection) {
            map.set(key, [item])
        } else {
            collection.push(item)
        }
    })

    return map
}

export default {
    name: "PDFViewer",

    components: {
        PDFDocument,
        PDFData,
        PDFDataMultiple,
        PDFPaginator,
        PDFPreview,
        PDFZoom,
        PDFWorkspace,
    },

    props: {
        documents: {
            type: Array,
            required: false,
        },
        merge: {
            type: Boolean,
            default: true,
        },
        // application: {
        // 	type: Object,
        // },
    },

    data() {
        return {
            documentsOrdered: true,
            showQuickPagePreview: false,
            showSaveAndExitModal: false,
            quickPagePreviewSrc: "",
            hideOpeningMessage: false,
            hideEditMessage: false,
            hideReorderMessage: false,
            showSelectorAddFileModal: true,
            headers: {},
            applicants: [],
            scale: undefined,
            optimalScale: undefined,
            fit: undefined,
            currentPage: 1,
            pageCount: undefined,
            pageIndex: undefined,
            isPreviewEnabled: false,
            setApplicantName: null,
            doubleEnabled: false,
            isPageOrderable: false,
            documentId: null,
            documentUrl: null,
            applicantId: null,
            pagesOrder: [],
            isAddingSignature: false,
            showApplicantSelectionModal: false,
            showSaveAndCompileModal: false,
            compileAvailable: false,
            isApplicantSet: {},
            documentData: {},
            signatures: [],
            saving: false,
            docs: [],
            allDocs: new Map(),
            documentTypes: [],
            workspaces: [],
            annotations: [],
            imageObjects: [],
            workspace: null,
            currentWorkspace: null,
            renameWorkspace: false,
            filteredPages: {},
            filteredPagesRaw: {},
            removedPages: {},
            selectedDocumentType: null,
            workspaceDocumentsLoaded: false,
            workspaceDocuments: [],
            documentSidebar: false,
            isAnnotating: false,
            showSuggestions: false,
            newSigner: {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
            },
        }
    },

    computed: {
        ...mapGetters({
            application: "initAppData"
        }),
        hasSignatures() {
            let signatures = this.annotations.filter((annotation) => {
                return annotation.type == "SIGN"
            })

            return signatures.length > 0 ? true : false
        },

        contactsGroup() {
            let group = groupBy(this.contacts, (contact) => {
                return (
                    contact.type != undefined && contact.type == "customer"
                        ? "Third Party"
                        : contact.type == "dealer"
                            ? "Seller"
                            : "Applicant/s"
                ).toUpperCase()
            })

            group = new Map([...group.entries()].sort())
            return group
        },

        dealerContacts() {
            if (this.application.invoiceInformation) {
                return this.application.invoiceInformation.reduce((result, item) => {
                    if (item.type != "dealer") {
                        item.contact.type = "dealer"
                        item.contact.user_id = item.contact.id
                        result.push(item.contact)
                    }
                    return result
                }, [])
            }
            return []
        },

        contacts() {
            let contacts = _.get(this.application, "contacts", [])
            return [...contacts, ...this.applicants, ...this.dealerContacts]
        },
    },

    methods: {
        close() {
            this.$emit("close", false)
        },
        openQuickPagePreview(src) {
            this.quickPagePreviewSrc = src
            this.showQuickPagePreview = true
        },
        closeQuickPagePreview() {
            this.showQuickPagePreview = false
            this.quickPagePreviewSrc = ""
        },

        openingMessage() {
            if ((this.currentWorkspace && this.workspaceDocuments.length > 0) || this.hideOpeningMessage) return
            this.$confirm(
                "To start firstly add an existing document from the <strong>Document Dropdown</strong> or upload a new file by clicking the <strong>File Upload</strong> icon",
                "Welcome to Merge & Markup",
                {
                    confirmButtonText: "OK",
                    cancelButtonText: "Thanks, hide message for now",
                    type: "info",
                    showClose: false,
                    dangerouslyUseHTMLString: true,
                }
            )
                .then(() => {
                    axios.put("/api/v2/user-settings", {mergeMarkup: {hideOpeningMessage: true}}, this.headers).then((res) => {
                        return res
                    })
                    this.hideOpeningMessage = true
                })
                .catch(() => {
                    this.hideOpeningMessage = true
                })
        },

        editMessage() {
            if (this.hideEditMessage) return
            this.$confirm(
                'Simply <strong>right-click</strong> to add a <strong>stamp</strong>, <strong>text</strong> or a <strong>signature</strong> to the document. Once placed you can then position by dragging the item to the required location.<br><hr><small style="line-height:10px;">*Please note: Click the <strong>Stamp Icon</strong> will place the stamp in the top left corner of the page</small>',
                "Edit Mode",
                {
                    confirmButtonText: "OK",
                    cancelButtonText: "Thanks, hide message for now",
                    type: "info",
                    showClose: false,
                    dangerouslyUseHTMLString: true,
                }
            )
                .then(() => {
                    axios.put("/api/v2/user-settings", {mergeMarkup: {hideEditMessage: true}}, this.headers).then((res) => {
                        return res
                    })
                    this.hideEditMessage = true
                })
                .catch(() => {
                    this.hideEditMessage = true
                })
        },

        reorderMessage() {
            if (this.hideReorderMessage) return
            this.$confirm(
                'Simply drag and drop to <strong>reorder</strong> pages, <strong>remove</strong> pages by clicking the <i class="fas fa-times-circle"></i> icon. <br><br><strong>Page Rotation</strong> is now available! Simply rotate left or right.',
                "Reorder Mode",
                {
                    confirmButtonText: "OK",
                    cancelButtonText: "Thanks, hide message for now",
                    type: "info",
                    showClose: false,
                    dangerouslyUseHTMLString: true,
                }
            )
                .then(() => {
                    axios.put("/api/v2/user-settings", {mergeMarkup: {hideReorderMessage: true}}, this.headers).then((res) => {
                        return res
                    })
                    this.hideReorderMessage = true
                })
                .catch(() => {
                    this.hideReorderMessage = true
                })
        },

        addSignature(signature) {
            this.signatures.push(signature)
            this.compileAvailable = false
        },

        removeSignature(index) {
            this.signatures.splice(index, 1)
            this.compileAvailable = false
        },

        addAnnotation(annotation) {
            this.annotations.push(annotation)
            this.compileAvailable = false
        },

        onDocumentRendered() {
            this.$emit("document-rendered", this.documentId)
        },

        onDocumentErrored(e) {
            this.$emit("document-errored", e)
        },

        updateScale({scale, isOptimal = false}) {
            const roundedScale = floor(scale, 2)
            if (isOptimal) this.optimalScale = roundedScale
            this.scale = roundedScale
        },

        updateFit(fit) {
            this.fit = fit
        },

        updatePageCount(pageCount) {
            this.pageCount = pageCount
        },

        updateCurrentPage(pageNumber) {
            this.currentPage = pageNumber
            this.pageIndex = this.merge ? this.filteredPages.findIndex((page) => page.pageNumber === pageNumber) + 1 : pageNumber
            if (!this.isAddingSignature) return
            this.doubleEnabled = false
            this.hideContextMenu()
        },

        togglePreview() {
            this.isPreviewEnabled = !this.isPreviewEnabled
            this.fit = "auto"
        },

        toggleDoubleView() {
            this.doubleEnabled = !this.doubleEnabled
            this.isPageOrderable = this.doubleEnabled ? false : this.isPageOrderable
        },

        togglePageOrderandSave() {
            this.compileAvailable = false
            this.update(true)
            this.isAnnotating = false
            this.doubleEnabled = false
            this.isPageOrderable = false
            this.isPreviewEnabled = true
            this.documentSidebar = true
            this.fit = this.isAnnotating ? "fixed" : "auto"
            this.$bus.$emit("pdf::garbage::removal", true)
        },

        togglePageOrder() {
            if (this.isPageOrderable) return
            this.compileAvailable = false
            this.isPageOrderable = true
            this.doubleEnabled = false
            this.documentSidebar = false

            this.reorderMessage()
            if (this.hideReorderMessage) {
                this.$message({
                    message: "Drag and Drop to reoder pages",
                    type: "success",
                })
            }
        },

        togglePageEditandSave() {
            this.saveAnnotation()
            this.compileAvailable = false
            this.isAnnotating = false
            this.showSuggestions = false
            this.doubleEnabled = false
            this.isPageOrderable = false
            this.isPreviewEnabled = true
            this.documentSidebar = true
            this.fit = this.isAnnotating ? "fixed" : "auto"
        },

        togglePageEdit() {
            if (this.isAnnotating) return
            this.compileAvailable = false
            this.isAnnotating = true
            this.doubleEnabled = false
            this.isPageOrderable = false
            this.isPreviewEnabled = true
            this.documentSidebar = false
            this.fit = this.isAnnotating ? "fixed" : "auto"
            this.editMessage()
            if (this.hideEditMessage) {
                this.$message({
                    message: "Entered Document Edit Mode",
                    type: "success",
                })
            }
        },

        toggleShowSuggestions() {
            if (!this.isAnnotating) return (this.showSuggestions = false)
            this.showSuggestions = !this.showSuggestions
        },

        toggleDocumentSidebar() {
            this.documentSidebar = !this.documentSidebar
        },

        toggleAddingSignature() {
            this.isAddingSignature = !this.isAddingSignature
            this.doubleEnabled = this.isAddingSignature ? false : this.doubleEnabled
            this.isPageOrderable = this.isAddingSignature ? false : this.isPageOrderable
            this.isPreviewEnabled = this.isAddingSignature ? true : this.isPreviewEnabled
            this.fit = this.isAddingSignature ? "fixed" : "auto"

            if (this.isAddingSignature) {
                this.$message({
                    message: "Entered Signature Placement Mode",
                    type: "success",
                })
            }
        },

        hideContextMenu() {
            $('[id^="pdf-context-menu-"]').hide()
            this.$bus.$emit("pdf::contextmenu::menuclose")
        },

        selectApplicant() {
            this.isApplicantSet = {}
            this.showApplicantSelectionModal = true
        },

        setApplicant() {
            if (this.setApplicantName == "add_new_signer") {
                this.createNewSigner()
            } else {
                this.isApplicantSet = {
                    id: this.setApplicantName.applicantId,
                    user_id: this.setApplicantName.user_id,
                    applicant: true,
                    page_num: this.currentPage,
                }
                this.showApplicantSelectionModal = false
            }
        },

        verifyNewSigner(newSigner) {
            let contacts = this.contacts
            // Verify the new signer doesn't have the same email as a current applicant
            let match = contacts.find((a) => {
                if (a.email === newSigner.email) return true
            })

            return match
        },

        async createNewSigner() {
            try {
                const signer = this.newSigner
                if (this.verifyNewSigner(signer)) return

                let res = await axios
                    .post(
                        `/api/contacts`,
                        {
                            first_name: signer.first_name,
                            last_name: signer.last_name,
                            email: signer.email,
                            phone: signer.phone,
                            type: "customer",
                            application_id: this.application.id,
                        },
                        this.headers
                    )
                    .then((response) => {
                        response.data.type = "customer"
                        this.application.contacts.push(response.data)
                        this.isApplicantSet = {
                            id: null,
                            user_id: response.data.id,
                            applicant: false,
                            page_num: this.currentPage,
                        }

                        this.setApplicantName = null
                        this.newSigner = {}
                        this.showApplicantSelectionModal = false
                    })
            } catch (err) {
                console.error(err)
            }
        },

        editWorkspace() {
            this.renameWorkspace = true
        },

        updateWorkspace() {
            // load workspace data;
            // load workspace documents;
            this.getWorkspace()
        },

        updateFiltered(data) {
            if (data == true) {
                data = this.filteredPagesRaw
            }
            this.filteredPages = data.map((page) => ({
                id: page.id,
                page_num: page.page_num,
                pageNumber: `${page.page_num}-${page.id}`,
                rotation: page.page.rotate,
                original_rotation: page.original_rotation,
            }))
            this.compileAvailable = false
        },
        updateRemoved(data) {
            this.removedPages = data
            this.compileAvailable = false
        },

        async update(showsave = false) {
            try {
                const index = this.workspaces.findIndex((ws) => {
                    return ws.id === this.currentWorkspace
                })

                this.workspaces[index].name = this.workspace.name

                let res = await axios.put(
                    `/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}`,
                    {
                        name: this.workspace.name,
                        filtered: this.filteredPages,
                        removed: this.removedPages,
                    },
                    this.headers
                )
                if (showsave) {
                    this.compileAvailable = true
                }
                this.renameWorkspace = false
            } catch (err) {
                console.error(err)
            }
        },

        initFileTypes() {
            let adminUrl = process.env.MIX_APP_URL
            let adminHeaders = {
                headers: {Authorization: "Bearer " + this.$store.getters.adminToken},
                params: {
                    mergeMarkup: 1,
                },
            }
            axios.get(adminUrl + "/api/documents", adminHeaders).then((res) => {
                this.documentTypes = res.data
                this.documentTypes.push({
                    id: null,
                    name: "Others",
                })
            })
        },

        async getWorkspace() {
            try {
                let res = await axios.get(
                    `/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}`,
                    this.headers
                )
                let defaultStyleOptions = {
                    color: "",
                    chosenSize: 14,
                    textStyle: {
                        bold: false,
                        italic: false,
                        underline: false,
                    },
                    fontSize: {
                        twelve: false,
                        forteen: false,
                        sixteen: false,
                        eighteen: false,
                    },
                }
                this.workspace = res.data
                this.annotations = res.data.annotations.map((annotation) => ({
                    client_id: annotation.client_id,
                    document_id: annotation.document_id,
                    document_workspace_id: annotation.document_workspace_id,
                    height: annotation.height,
                    id: annotation.id,
                    applicant_id: annotation.type === "SIGN" ? annotation.applicant_id : "",
                    user_id: annotation.type === "SIGN" ? annotation.user_id : "",
                    object_id: annotation.image_object_id,
                    src:
                        annotation.image_object_id && annotation.type === "IMAGE"
                            ? this.imageObjects.find((stamp) => {
                                return stamp.id === annotation.image_object_id
                            }).temporary_url
                            : "",
                    page: annotation.page,
                    page_num: annotation.page_num,
                    text: annotation.text,
                    type: annotation.type,
                    width: annotation.width,
                    x: annotation.x,
                    y: annotation.y,
                    new: false,
                    styles: annotation.styles,
                    styleOptions: annotation.style_options ? JSON.parse(annotation.style_options) : defaultStyleOptions,
                }))
            } catch (err) {
                console.error(err)
            }
        },

        async deleteWorkspace() {
            const index = this.workspaces.findIndex((ws) => {
                return ws.id === this.currentWorkspace
            })

            const workspace = this.workspaces[index]
            let result = await swal({
                title: "Are you sure?",
                html:
                    "This action will delete the workspace and your current work inside the workspace." +
                    "<br/><br/>You won't be able to revert this!",
                type: "warning",
                showCancelButton: true,
                cancelButtonText: "Cancel",
                cancelButtonColor: "#d33",
                confirmButtonText: "Delete",
                confirmButtonColor: "#284da5",
                reverseButtons: false,
            })

            if (result.value) {
                this.showSelectorAddFileModal = true
                try {
                    let res = await axios.delete(
                        `/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${workspace.id}`,
                        this.headers
                    )
                    this.workspaces.splice(index, 1)
                    this.currentWorkspace = null
                } catch (err) {
                    console.error(err)
                }
            }
        },

        async getDocuments() {
            if (this.merge) {
                axios.get(`/api/v2/applications/${this.$store.getters.initAppData.id}/_modules/documents`, this.headers).then((res) => {
                    this.docs = res.data
                    this.allDocs = groupBy(res.data, (doc) => {
                        return (
                            doc.document_type && doc.document_category != "markup" ? doc.document_type.document_type || "Other" : "Other"
                        ).toUpperCase()
                    })
                    this.$bus.$emit("pdf::docs::loaded", true)
                    return
                })
            }

            this.docs = this.documents
            return
        },

        async loadImageObjects() {
            return new Promise(async (resolve, reject) => {
                try {
                    let adminUrl = process.env.MIX_APP_URL
                    let res = await axios.get(`${adminUrl}/api/image-objects`, {
                        headers: {
                            Authorization: `Bearer ${this.$store.getters.adminToken}`,
                        },
                    })
                    resolve(res.data)
                    this.imageObjects = res.data
                } catch (Err) {
                    reject(Err)
                }
            })
        },

        async addWorkspace() {
            try {
                let res = await axios.post(`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces`, {}, this.headers)
                this.workspaceDocuments = []
                this.workspaceDocumentsLoaded = false
                this.pagesOrder = []
                this.currentPage = undefined
                this.pageCount = null
                this.signatures = []
                this.annotations = []
                this.documentData = {}
                this.isAnnotating = false
                this.doubleEnabled = false
                this.isPageOrderable = false
                this.isPreviewEnabled = false
                this.documentSidebar = false
                this.workspaces.unshift(res.data)
                this.workspace = res.data
                this.currentWorkspace = res.data.id
            } catch (err) {
                console.error(err)
            }
        },

        async getWorkspaces() {
            try {
                let res = await axios.get(`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces`, this.headers)

                this.workspaces = res.data
            } catch (err) {
                console.error(err)
            }
        },

        async getWorkspaceDocuments() {
            if (!this.currentWorkspace) return false
            try {
                let loaded = await axios
                    .get(
                        `/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}/documents`,
                        this.headers
                    )
                    .then((res) => {
                        if (res.data.length) {
                            this.workspaceDocuments = res.data
                            this.workspaceDocumentsLoaded = true
                        }
                    })
                    .finally((finshed) => {
                        this.$bus.$emit("pdf::workspace::ready", true)
                    })
            } catch (err) {
                console.error(err)
            }
        },

        async saveAnnotation(isAuto = false) {
            //   this.loading = true;
            let postData = []
            return new Promise((resolve, reject) => {
                this.annotations.forEach((annotation, index) => {
                    const w = 1190
                    const h = 1684
                    let Xpercent = (annotation.x / w) * 100
                    let Ypercent = (annotation.y / h) * 100

                    let defaultStyleOptions = {
                        color: "",
                        chosenSize: 14,
                        textStyle: {
                            bold: false,
                            italic: false,
                            underline: false,
                        },
                        fontSize: {
                            twelve: false,
                            forteen: false,
                            sixteen: false,
                            eighteen: false,
                        },
                    }

                    postData.push({
                        id: this.annotations[index].new === false && !this.annotations[index]._server_id ? this.annotations[index].id : null,
                        client_id:
                            this.annotations[index].new === false
                                ? this.annotations[index].client_id // stored ulid
                                : this.annotations[index].id, // the generated ulid
                        x: annotation.x,
                        y: annotation.y,
                        x_percent: Xpercent,
                        y_percent: Ypercent,
                        height: annotation.height ? annotation.height : null,
                        width: annotation.width ? annotation.width : null,
                        applicant_id: annotation.applicant_id,
                        user_id: annotation.user_id,
                        object_id: annotation.object_id ? annotation.object_id : null,
                        text: annotation.text ? annotation.text : null,
                        page_num: annotation.page_num,
                        type: annotation.type,
                        styles: annotation.styles ? annotation.styles : null,
                        style_options: annotation.styleOptions ? annotation.styleOptions : defaultStyleOptions,
                    })
                })
                axios
                    .post(
                        `/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.workspace.id}/annotations`,
                        {
                            annotations: postData,
                        },
                        this.headers
                    )
                    .then((res) => {
                        if (!isAuto)
                            this.$message({
                                type: "success",
                                message: "Annotations Saved",
                            })

                        res.data.forEach((annotation) => {
                            let newSig = this.annotations.find((a) => {
                                return a.new && a.id === annotation.client_id
                            })
                            if (newSig) {
                                newSig.id = annotation.id
                                newSig.new = false
                                newSig.client_id = annotation.client_id
                            }
                        })

                        resolve()
                    })
                    .catch((err) => {
                        console.error(err)
                        this.$notify({
                            title: "Request Error",
                            message: "Cannot Save Annotation",
                            type: "error",
                        })
                        resolve(err)
                    })
            })
        },

        async removeAnnotation(index) {
            try {
                let annotation = this.annotations[index]
                if (annotation) {
                    if (!annotation.new) {
                        let res = await axios.delete(
                            `/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.workspace.id}/annotations/${
                                annotation._server_id ? annotation._server_id : annotation.id
                            }`,
                            this.headers
                        )
                    }
                    this.annotations.splice(index, 1)
                }
            } catch (err) {
                console.error(err)
            }
        },

        saveAndExit() {
            this.saving = true
            this.update(true)
            this.saving = false
            this.showSaveAndExitModal = false
            this.currentWorkspace = null
            this.workspaceDocuments = []
            this.workspaceDocumentsLoaded = false
            this.pagesOrder = []
            this.currentPage = undefined
            this.pageCount = null
            this.signatures = []
            this.annotations = []
            this.documentData = {}
            this.isAnnotating = false
            this.doubleEnabled = false
            this.isPageOrderable = false
            this.isPreviewEnabled = false
            this.documentSidebar = false
            this.showSelectorAddFileModal = false
            this.$message({
                type: "success",
                message: "Document Workspace Saved",
            })

            this.$bus.$emit("pdf::close::merge", true)
        },

        saveAndCompile(hasSignatures = false) {
            if (!this.compileAvailable) {
                this.saving = true
                this.update(true)
                this.saving = false
            } else {
                try {
                    let res = axios.post(`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.workspace.id}/compile`, {
                        name: this.workspace.name,
                        document_type_id: this.selectedDocumentType
                    }, this.headers)

                    this.saving = false
                    this.showSaveAndCompileModal = false
                    this.$message({
                        type: "success",
                        message: "Document Compiling",
                    })

                    setTimeout(() => {
                        this.$bus.$emit("pdf::document::compiled", true)
                        if (hasSignatures) {
                            this.$router.push({name: "Conditioning"})
                            this.$bus.$emit("pdf::document::signature-modal", true)
                        }
                        this.saveAndExit()
                    }, 5000)
                } catch (err) {
                    console.error(err)
                }
            }
        },
    },

    watch: {
        url() {
            this.currentPage = undefined
        },

        hasSignatures(newVal, oldVal) {
            if (this.hasSignatures) {
                let defaultDoc = this.documentTypes.filter((doc) => {
                    return doc.name == "Digital Documents to sign"
                })

                this.selectedDocumentType = defaultDoc[0].id
            }
        },

        workspaceDocuments() {
            if (this.workspaceDocuments.length === 0) {
                this.isAnnotating = false
                this.doubleEnabled = false
                this.isPageOrderable = false
                this.isPreviewEnabled = false
            }
        },

        currentWorkspace(newVal, oldVal) {
            this.$bus.$emit("pdf::workspace::ready", false)
            if (this.currentWorkspace == null) {
                this.showSelectorAddFileModal = true
                return
            }
            this.workspaceDocuments = []
            this.workspaceDocumentsLoaded = false
            this.pagesOrder = []
            this.currentPage = undefined
            this.pageCount = null
            this.signatures = []
            this.annotations = []
            this.documentData = {}
            this.isAnnotating = false
            this.doubleEnabled = false
            this.isPageOrderable = false
            this.isPreviewEnabled = false
            this.documentSidebar = false
            this.showSelectorAddFileModal = false
            this.getWorkspaceDocuments()
            this.$nextTick(() => {
                this.$bus.$emit("pdf::docs::loaded", true)
                this.documentSidebar = true
                this.isPreviewEnabled = true
                this.documentData = {
                    id: newVal,
                    type: "documents",
                    signatures: this.workspaceDocuments.map((doc) => ({id: doc.id, signatures: doc.signatures})),
                }
                setTimeout(() => {
                    this.openingMessage()
                }, 1000)
            })
        },

        documentId(newVal, oldVal) {
            this.showSelectorAddFileModal = false
            let doc = this.documents.find((item) => {
                return item.id === newVal
            })
            this.documentUrl = doc.url
            this.documentData = {
                id: this.documentId,
                type: doc ? (!doc.is_app_document ? "documents" : "app-documents") : null,
                signatures: doc.signatures,
            }
            this.signatures = doc.signatures
        },

        applicantId(newVal, oldVal) {
            this.$emit("change-applicant", {
                applicantId: this.applicantId,
            })
        },
    },
    activated() {
        if (this.merge) {
            this.initFileTypes()
            this.getWorkspaces()
            this.loadImageObjects()
        }
    },
    created() {
    },
    beforeDestroy() {
        this.$bus.$off("pdf::workspace::ready")
        this.$bus.$off("pdf::page::visibility::focus")
        this.$bus.$off("pdf::pages::visibility::zoom")
        this.$bus.$off("pdf::pages::visibility::scale")

        this.$bus.$off("pdf::pages::filteredRaw")
        this.$bus.$off("pdf::pages::filtered")
        this.$bus.$off("pdf::pages::removed")

        this.$bus.$off("pdf::pages::count")
        this.$bus.$off("pdf::page::quickpreview")

        this.$bus.$off("pdf::new::loading")
        this.$bus.$off("pdf::add")
        this.$bus.$off("pdf::docs::reload")

        this.$bus.$off("pdf::toggle::preview")
        this.$bus.$off("pdf::toggle::doubleview")
        this.$bus.$off("pdf::toggle::page::order")
        this.$bus.$off("pdf::toggle::page::edit")
        this.$bus.$off("pdf::toggle::documentsidebar")

        this.$bus.$off("pdf::annotation::add")
        this.$bus.$off("pdf::annotation::remove")
        this.$bus.$off("pdf::annotation::suggestions::toggle")

        this.$bus.$off("pdf::signature::add")
        this.$bus.$off("pdf::signature::remove")
    },
    mounted() {
        this.hideOpeningMessage = _.get(this.Ambition, "Globals.tenant.user.user_settings.mergeMarkup.hideOpeningMessage", false)
        this.hideEditMessage = _.get(this.Ambition, "Globals.tenant.user.user_settings.mergeMarkup.hideEditMessage", false)
        this.hideReorderMessage = _.get(this.Ambition, "Globals.tenant.user.user_settings.mergeMarkup.hideReorderMessage", false)

        let headers = {
            headers: {Authorization: "Bearer " + this.$store.getters.token},
        }
        this.headers = headers
        this.getDocuments()
        this.applicants = this.$store.getters["application/applicants"]
        document.body.classList.add("overflow-hidden")

        this.$bus.$on("pdf::workspace::ready", (payload) => {
            this.documentsOrdered = !payload
        })
        this.$bus.$on("pdf::page::visibility::focus", this.updateCurrentPage)
        this.$bus.$on("pdf::pages::visibility::zoom", this.updateFit)
        this.$bus.$on("pdf::pages::visibility::scale", this.updateScale)

        this.$bus.$on("pdf::pages::filteredRaw", (payload) => {
            this.filteredPagesRaw = payload
        })
        this.$bus.$on("pdf::pages::filtered", this.updateFiltered)
        this.$bus.$on("pdf::pages::removed", this.updateRemoved)

        this.$bus.$on("pdf::pages::count", this.updatePageCount)
        this.$bus.$on("pdf::page::quickpreview", this.openQuickPagePreview)

        this.$bus.$on("pdf::new::loading", () => {
            this.workspaceDocumentsLoaded = false
        })
        this.$bus.$on("pdf::add", this.getWorkspaceDocuments)
        this.$bus.$on("pdf::docs::reload", this.getDocuments)

        this.$bus.$on("pdf::toggle::preview", this.togglePreview)
        this.$bus.$on("pdf::toggle::doubleview", this.toggleDoubleView)
        this.$bus.$on("pdf::toggle::page::order", this.togglePageOrder)
        this.$bus.$on("pdf::toggle::page::edit", this.togglePageEdit)
        this.$bus.$on("pdf::toggle::documentsidebar", this.toggleDocumentSidebar)

        this.$bus.$on("pdf::annotation::add", this.addAnnotation)
        this.$bus.$on("pdf::annotation::remove", this.removeAnnotation)
        this.$bus.$on("pdf::annotation::suggestions::toggle", this.toggleShowSuggestions)

        this.$bus.$on("pdf::signature::add", this.addSignature)
        this.$bus.$on("pdf::signature::remove", this.removeSignature)
    },
}
</script>

<style lang="scss" scoped>
header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    padding: 1em;
    position: relative;
    z-index: 99;
    height: 70px;
    background: #ffffff;
    border-bottom: 1px solid #d3d3d3;
}

.left {
    width: 900px;
    display: flex;
    flex-wrap: nowrap;
}

.left a,
.right a {
    border-radius: 5px !important;
    padding: 5px;
    border: 1px solid #d3d3d3;
    font-size: 1.5rem;
    color: #a0a0a0;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 4px;
}

.left a.success {
    background: #5bbc2a;
    color: #fff;
    border-color: #5bbc2a;
}

.left a:hover,
.left a.active,
.right a:hover,
.right a.active {
    color: #fff;
    background: #797979;
}

.right {
    flex-grow: 1;
    display: flex;
    justify-content: flex-end;
    flex-wrap: nowrap;
}

.header-item {
    margin: 0 2.5em;
}

.new-signer-form {
    h4 {
        margin: 1rem auto;
        text-align: left;
        padding-bottom: 1rem;
        font-weight: 600 !important;
    }
}

.pdf-document-input {
    width: 100%;
    padding: 10px 15px;
}

.pdf-document-popper {
    width: 100%;
    max-width: 100%;
    padding: 0rem !important;
    margin-left: 0rem !important;
}

.pdf-viewer .pdf-viewer__document,
.pdf-viewer .pdf-viewer__preview {
    top: 70px;
}

.pdf-viewer.merge .pdf-viewer__document,
.pdf-viewer.merge .pdf-viewer__preview {
    top: 0px;
}

.pdf-viewer__main {
    height: calc(100vh - 130px);
}

.pdf-viewer.merge .pdf-viewer__main {
    height: calc(100vh - 200px);
}

.pdf-viewer__preview {
    display: block;
    width: 15%;
    right: 85%;
    background: #f5f5f5;
    border-right: 1px solid #d3d3d3;
}

.pdf-viewer__preview.row {
    width: 100%;
    right: auto;
}

.pdf-viewer__preview.row > div {
    display: flex;
    flex-wrap: wrap;
    margin-right: -15px;
    margin-left: -15px;
}

.pdf-viewer__document {
    width: 100%;
    left: 0;
    background: #f5f5f5;
}

.pdf-viewer__document.preview-enabled {
    width: 85%;
    left: 15%;
}

.merge-pagination {
    position: absolute;
    right: 15px;
    bottom: 15px;
}

@media print {
    header {
        display: none;
    }
}
</style>
<style>
.pdf-document-dropdown {
    width: 470px !important;
    max-width: 100% !important;
    padding: 0rem !important;
    margin-left: 0rem !important;
}

.please-note {
    padding: 1rem;
    background: #e8e8e8;
}

.error input {
    color: #680808;
    background-color: #f56c6c;
    border-color: #680808;
}
</style>
